'use client';

import { useSyncExternalStore } from 'react';

/**
 * The theme actually painted right now — 'dark' or 'light', with 'system'
 * already resolved.
 *
 * Reads the `data-theme` attribute that layout.tsx's inline script stamps on
 * <html> before first paint, rather than the cookie: the attribute is the one
 * source both the inline script and useDarkMode's setTheme write to, so it is
 * correct during hydration and stays correct when the user flips the theme.
 *
 * useSyncExternalStore rather than an Effect (see the useEffect rules in
 * CLAUDE.md) — the DOM attribute is an external store. Its getServerSnapshot
 * returns 'dark', matching the inline script's own default, so server render
 * and first client render agree.
 *
 * Needed because getFactiiiColor has to hand a concrete hex to
 * getReadableTextColor for contrast math; a CSS variable can't be measured.
 */
const readTheme = (): 'dark' | 'light' =>
  document.documentElement.getAttribute('data-theme') === 'light'
    ? 'light'
    : 'dark';

const subscribe = (onStoreChange: () => void) => {
  const observer = new MutationObserver(onStoreChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme']
  });
  return () => observer.disconnect();
};

const getServerSnapshot = (): 'dark' => 'dark';

const useEffectiveTheme = (): 'dark' | 'light' =>
  useSyncExternalStore(subscribe, readTheme, getServerSnapshot);

export default useEffectiveTheme;
